import { toJS, observable, action } from "mobx";
import uuidv1 from "uuid/v1";
import Api from "../service/Api";
import BindDataToStore from "../utils/BindDataToStore";

const generateUUID = () =>
  // Generate an id, without '-' and of 13 chars long
  // to currently mirror how LineItem.php generates unique ids.
  uuidv1()
    .replace(/-/g, "")
    .substring(0, 13);

class LineItem {
  @observable id = "";

  @observable description = "";

  @observable price = "";

  constructor({ id, description, price }) {
    this.id = id || generateUUID();
    this.description = description || "";
    this.price = price || "";
  }
}

class LineItemGroup {
  @observable id = "";

  @observable label = "";

  @observable lineItems = [];

  constructor({ id, label, lineItems }) {
    this.id = id || generateUUID();
    this.label = label || "";

    const actualLineItems = lineItems || [];

    for (let i = 0; i < actualLineItems.length; i += 1) {
      this.lineItems.push(new LineItem(actualLineItems[i]));
    }
  }
}

class OrderFormVariation {
  @observable id = "";

  @observable name = "";

  @observable tourTypes = [];

  @observable extras = [];

  @observable services = [];

  constructor({ id, name, tourTypes, extras, services }) {
    this.id = id || generateUUID();
    this.name = name || "";

    for (let i = 0; i < tourTypes.length; i += 1) {
      this.tourTypes.push(new LineItem(tourTypes[i]));
    }

    for (let i = 0; i < extras.length; i += 1) {
      this.extras.push(new LineItem(extras[i]));
    }

    for (let i = 0; i < services.length; i += 1) {
      this.services.push(new LineItemGroup(services[i]));
    }
  }

  getLineItems() {
    const lineItems = [];

    for (let i = 0; i < this.tourTypes.length; i += 1) {
      lineItems.push(this.tourTypes[i]);
    }

    for (let i = 0; i < this.extras.length; i += 1) {
      lineItems.push(this.extras[i]);
    }

    for (let i = 0; i < this.services.length; i += 1) {
      const service = this.services[i];

      for (let j = 0; j < service.lineItems.length; j += 1) {
        lineItems.push(service.lineItems[j]);
      }
    }

    return lineItems;
  }

  findListContainingLineItem(lineItemId) {
    for (let i = 0; i < this.tourTypes.length; i += 1) {
      if (this.tourTypes[i].id === lineItemId) {
        return this.tourTypes;
      }
    }

    for (let i = 0; i < this.extras.length; i += 1) {
      if (this.extras[i].id === lineItemId) {
        return this.extras;
      }
    }

    for (let i = 0; i < this.services.length; i += 1) {
      const service = this.services[i];

      for (let j = 0; j < service.lineItems.length; j += 1) {
        if (service.lineItems[j].id === lineItemId) {
          return service.lineItems;
        }
      }
    }

    return null;
  }

  findLineItem(id) {
    return this.getLineItems().find(lineItemLookup => lineItemLookup.id === id);
  }
}

const validateSectionSpelling = section => {
  if (!(section === "tourTypes" || section === "extras" || section === "services")) {
    throw new Error(`Section must be tourTypes, extras or services. Got ${section}`);
  }
};

const validateLineItemFieldSpelling = property => {
  if (!(property === "description" || property === "price")) {
    throw new Error(`Property must be 'description' or 'price'. Got ${property}`);
  }
};

const isBlank = value => {
  if (typeof value === "undefined") {
    return true;
  }

  if (value === null) {
    return true;
  }

  if (String(value).trim().length === 0) {
    return true;
  }

  return false;
};

const isNumeric = value => {
  const regex = /^\d+(\.\d{1,2})?$/;
  return regex.test(value);
};

const updateLineItemsErrors = lineItems => {
  const errors = {};
  let isValid = true;

  for (let i = 0; i < lineItems.length; i += 1) {
    const lineItem = lineItems[i];
    // start by initializing the line item properties to false (not invalid).
    // Later on we'll set them to false if they have errors.
    errors[lineItem.id] = {
      description: false,
      price: false,
    };

    if (isBlank(lineItem.description)) {
      errors[lineItem.id].description = true;
      isValid = false;
    }

    // Blank is allowed, but if not blank then it must be numeric
    if (!isBlank(lineItem.price)) {
      if (!isNumeric(lineItem.price)) {
        errors[lineItem.id].price = true;
        isValid = false;
      }
    }
  }

  return {
    isValid,
    errors,
  };
};

export default class OrderForm {
  @observable defaultHeading1 = "";

  @observable defaultHeading2 = "";

  @observable customHeading1 = "";

  @observable customHeading2 = "";

  @observable variations = [];

  @observable variationsErrors = {};

  @observable enableBuiltInScheduling = false;

  @observable scheduleRequired = false;

  @observable licensingInfo = "";

  @observable schedulingInstructions = "";

  @observable thankYouMessage = "";

  @observable loading = false;

  @observable posting = false;

  constructor(data) {
    if (data && Object.keys(data).length > 0) {
      this.init(data);
    }
  }

  init(data) {
    const { variations, ...rest } = data;

    if (variations && variations.length > 0) {
      for (let i = 0; i < variations.length; i += 1) {
        const variation = new OrderFormVariation(variations[i]);
        this.variations.push(variation);
      }
    }

    BindDataToStore(this, rest);
  }

  getAllServices() {
    const services = [];

    for (let i = 0; i < this.variations.length; i += 1) {
      const variation = this.variations[i];
      for (let j = 0; j < variation.services.length; j += 1) {
        services.push(variation.services[j]);
      }
    }

    return services;
  }

  findListContainingLineItem(lineItemId) {
    for (let i = 0; i < this.variations.length; i += 1) {
      const list = this.variations[i].findListContainingLineItem(lineItemId);

      if (list) {
        return list;
      }
    }

    return null;
  }

  findLineItem(lineItemId) {
    for (let i = 0; i < this.variations.length; i += 1) {
      const lineItem = this.variations[i].findLineItem(lineItemId);

      if (lineItem) {
        return lineItem;
      }
    }

    return null;
  }

  @action updateCustomHeading1(value) {
    this.customHeading1 = value;
  }

  @action updateCustomHeading2(value) {
    this.customHeading2 = value;
  }

  @action toggleEnableBuiltInScheduling() {
    this.enableBuiltInScheduling = !this.enableBuiltInScheduling;

    if (!this.enableBuiltInScheduling) {
      this.scheduleRequired = false;
    }
  }

  @action toggleScheduleRequired() {
    this.scheduleRequired = !this.scheduleRequired;

    if (this.scheduleRequired) {
      this.enableBuiltInScheduling = true;
    }
  }

  @action updateSchedulingInstructions(value) {
    this.schedulingInstructions = value;
  }

  @action updateLicensingInfo(value) {
    this.licensingInfo = value;
  }

  @action updateThankYouMessage(value) {
    this.thankYouMessage = value;
  }

  @action updateOrderFormVariationName(id, value) {
    this.variations.find(item => item.id === id).name = value;
    // Validate variations so errors can be catch early.
    this.validateVariations();
  }

  @action updateServiceLabel(serviceId, value) {
    this.getAllServices().find(service => service.id === serviceId).label = value;
    this.validateVariations();
  }

  @action updateLineItem(lineItemId, property, value) {
    validateLineItemFieldSpelling(property);
    const lineItem = this.findLineItem(lineItemId);

    if (!lineItem) {
      throw new Error(`Could not find line item ${lineItemId}`);
    }

    lineItem[property] = value;

    // Validate variations so errors can be catch early.
    this.validateVariations();
  }

  /**
   * Adds a row of a description & price inputs pair to a given section.
   * @param {number} id - The variation id where we'll add a new row.
   * @param {string} section - 'tourTypes' or 'extras'.
   */
  @action addLineItem(variationId, section) {
    validateSectionSpelling(section);
    // Let's add an empty line item.
    this.variations.find(variation => variation.id === variationId)[section].push(new LineItem({}));
  }

  @action removeLineItem(lineItemId) {
    const list = this.findListContainingLineItem(lineItemId);

    if (!list) {
      throw new Error(`Could not find line item ${lineItemId}`);
    }

    const lineItem = list.find(lineItemLookup => lineItemLookup.id === lineItemId);

    // Remove from list
    list.splice(list.indexOf(lineItem), 1);

    // Validate variations so errors can be catch early.
    this.validateVariations();
  }

  @action addLineItemToService(serviceId) {
    const service = this.getAllServices().find(serviceLookup => serviceLookup.id === serviceId);

    if (!service) {
      throw new Error(`Could not find service for id ${serviceId}`);
    }

    service.lineItems.push(new LineItem({}));
  }

  @action addService(variationId) {
    const variation = this.variations.find(variationLookup => variationLookup.id === variationId);

    if (!variation) {
      throw new Error(`Could not find variation ${variationId}`);
    }

    variation.services.push(new LineItemGroup({ label: "", lineItems: [new LineItem({})] }));
  }

  @action removeService(serviceId) {
    for (let i = 0; i < this.variations.length; i += 1) {
      const variation = this.variations[i];

      const service = variation.services.find(serviceLookup => serviceLookup.id === serviceId);

      if (service) {
        variation.services.splice(variation.services.indexOf(service), 1);

        return;
      }
    }

    throw new Error(`Could not find service for id ${serviceId}`);
  }

  @action addOrderFormVariation() {
    const data = {
      name: "",
      tourTypes: [
        {
          description: "",
          price: "",
        },
      ],
      extras: [
        {
          description: "",
          price: "",
        },
      ],
    };

    data.services = [
      {
        label: "",
        lineItems: [
          {
            description: "",
            price: "",
          },
        ],
      },
    ];

    const variation = new OrderFormVariation(data);
    this.variations.push(variation);
  }

  @action removeOrderFormVariation(id) {
    if (!window.confirm("Data in this section will be lost. Do you want to continue?")) {
      return;
    }
    const variationToRemove = this.variations.find(item => item.id === id);
    this.variations.splice(this.variations.indexOf(variationToRemove), 1);
  }

  /**
   * Validate that descriptions are set. Prices are allowed to be null, but will get validated if set.
   */
  @action validateVariations() {
    const updatedErrors = {};
    let lineItemsValidation;
    let isValid = true;
    const uniqueNames = [];

    for (let i = 0; i < this.variations.length; i += 1) {
      const variation = this.variations[i];
      updatedErrors[variation.id] = {
        name: false,
        tourTypes: {},
        extras: {},
        services: {},
      };

      // Validate that all names are unique. Note that null is allowed as a valid name (for the default variation),
      // but only one variation can have it set to null. This snipped should pick that too.

      if (uniqueNames.indexOf(variation.name) !== -1) {
        updatedErrors[variation.id].name = true;
        isValid = false;
      }

      // cast null to empty string, otherwise null will be treated as an object and null is not the same as ''.
      uniqueNames.push(variation.name === null ? "" : variation.name);

      lineItemsValidation = updateLineItemsErrors(variation.tourTypes);
      updatedErrors[variation.id].tourTypes = lineItemsValidation.errors;

      if (!lineItemsValidation.isValid) {
        isValid = false;
      }

      lineItemsValidation = updateLineItemsErrors(variation.extras);
      updatedErrors[variation.id].extras = lineItemsValidation.errors;

      if (!lineItemsValidation.isValid) {
        isValid = false;
      }

      for (let j = 0; j < variation.services.length; j += 1) {
        const service = variation.services[j];

        updatedErrors[variation.id].services[service.id] = {
          label: false,
          lineItems: {},
        };

        if (isBlank(service.label)) {
          updatedErrors[variation.id].services[service.id].label = true;
          isValid = false;
        }

        lineItemsValidation = updateLineItemsErrors(service.lineItems);
        updatedErrors[variation.id].services[service.id].lineItems = lineItemsValidation.errors;

        if (!lineItemsValidation.isValid) {
          isValid = false;
        }
      }
    }

    this.variationsErrors = updatedErrors;

    return isValid;
  }

  @action handleSubmit(successCallback) {
    if (!this.validateVariations()) {
      alert("Errors found in the Tour Plan section. Please fix them before continuing.");
      return;
    }

    const payload = {
      variations: toJS(this.variations),
      enableBuiltInScheduling: this.enableBuiltInScheduling,
      scheduleRequired: this.scheduleRequired,
      licensingInfo: this.licensingInfo,
      schedulingInstructions: this.schedulingInstructions,
      thankYouMessage: this.thankYouMessage,
      customHeading1: this.customHeading1,
      customHeading2: this.customHeading2,
    };

    this.posting = true;

    Api.post("/provider/order-form", payload)
      .then(() => {
        successCallback && successCallback();
      })
      .catch(() => {
        alert("An unexpected error occurred. Please try later.");
        this.posting = false;
      });
  }
}
